import { Skeleton } from "@mui/material"
import { Subscribe } from "@react-rxjs/core"
import { useTranslation } from "react-i18next"

import { InstrumentLogo } from "@/App/Equities/shared/components"
import { useInstrument } from "@/App/Equities/shared/services/instruments"
import { EquityPosition } from "@/App/Equities/shared/services/TradingGateway"
import {
  formatCurrency,
  formatProfitAndLoss,
  getChangeTextColor,
} from "@/App/Equities/shared/utils"
import { Stack, Typography } from "@/generated/material"
import { PositionSide } from "@/generated/TradingGateway"

interface Props extends EquityPosition {
  "data-testid": string
}

export const PositionListItem = ({
  symbol,
  side,
  qty,
  unrealizedPl,
  currentPrice,
  lastDayPrice,
  "data-testid": testId,
}: Props) => {
  const { t } = useTranslation()

  const pl = Number(unrealizedPl)
  const direction = side === PositionSide.Long ? t("buy") : t("sell")

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="md"
      padding="xs"
      data-testid={testId}
    >
      <InstrumentLogo symbol={symbol} data-testid={`${testId}.logo`} />

      <Stack flexGrow={1} gap="md" overflow="hidden">
        <Stack direction="row" gap="md" alignItems="baseline">
          <Stack flexGrow={1} overflow="hidden">
            <Typography
              variant="Text lg/Regular"
              color="text.primary" // TODO: this should be replaced by a link style coming from a MuiLink
              sx={{ lineHeight: "normal" }}
              noWrap
              data-testid={`${testId}.name`}
            >
              <Subscribe fallback={<Skeleton />}>
                <InstrumentName symbol={symbol} />
              </Subscribe>
            </Typography>
          </Stack>
          <Typography
            variant="Text lg/Regular"
            color="text.primary" // TODO: this should be replaced by a link style coming from a MuiLink
            sx={{ lineHeight: "normal" }}
            data-testid={`${testId}.price`}
          >
            {formatCurrency(currentPrice ?? lastDayPrice)}
          </Typography>
        </Stack>
        <Stack direction="row" gap="md" alignItems="baseline">
          <Typography
            flexGrow={1}
            variant="Text lg/Regular"
            color="text-tertiary"
            sx={{ lineHeight: "normal" }}
            data-testid={`${testId}.directionAndQty`}
          >
            {`${direction} ${qty}`}
          </Typography>
          <Typography
            variant="Text lg/Regular"
            color={getChangeTextColor({ change: pl })}
            sx={{ lineHeight: "normal" }}
            data-testid={`${testId}.pl`}
          >
            {formatProfitAndLoss(unrealizedPl)}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  )
}

const InstrumentName = ({ symbol }: { symbol: string }) => {
  const displayName = useInstrument(symbol)?.displayName
  return displayName
}
